import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';

import logoContrato from '@/assets/logo-contrato.png';
import { Botao } from '@/componentes/Botao';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { formatarData } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';

import { apiDaFolhaDeTurno } from './folhasDeTurno';

/**
 * A FOLHA DE TURNO IMPRESSA (0041; decisão 9): uma máquina e um período — o
 * que é impresso ninguém precisa escrever. O código vai grande no topo, porque
 * é por ele que a leitura acha a folha. Cada linha é um turno fechado.
 *
 * NÃO HÁ COLUNA DE HORA DE MOTOR, de propósito: ela é a diferença dos
 * horímetros, e o sistema a calcula. Uma coluna para escrevê-la convidaria a
 * afirmar oito horas de motor num horímetro que andou seis.
 *
 * Fora da casca, como a folha de combustível: página para o papel, sem menu.
 */

const rota = getRouteApi('/impressao/folha-de-turno/$id');
const LINHAS = 31;
const dataCurta = (iso: string) => iso.split('-').reverse().join('/');

export function PaginaFolhaDeTurnoImpressa() {
  const { id } = rota.useParams();
  const folha = useQuery({ queryKey: ['operacao', 'folhas', 'folha', id], queryFn: () => apiDaFolhaDeTurno.folha(id) });

  if (folha.isError || (!folha.isPending && !folha.data)) {
    return (
      <div className="p-8">
        <EstadoVazio
          titulo="Não foi possível abrir a folha"
          descricao={folha.isError ? explicarFalha(folha.error).comoResolver : 'Abra a folha pela lista de folhas de turno.'}
        />
      </div>
    );
  }
  /* Carregando (p.32): a folha já tem a forma — o cabeçalho, os rótulos e a
     grade em branco não dependem da resposta. Só o que é da máquina vira
     barra, e o imprimir espera: papel com barra cinza não serve ao campo. */
  const f = folha.data?.folha ?? null;
  const turnos = folha.data?.turnos ?? null;
  const causas = folha.data?.causas ?? [];

  return (
    <div className="h-full overflow-auto bg-card print:overflow-visible">
      <div className="mx-auto flex max-w-[1000px] flex-col gap-4 p-8 text-tinta print:max-w-none print:p-0">
        <div className="flex justify-end gap-2 print:hidden">
          <Botao
            nivel="primario"
            disabled={f === null}
            onClick={() => {
              window.print();
            }}
          >
            Imprimir folha
          </Botao>
        </div>

        <header className="flex items-start justify-between gap-4 border-b-2 border-tinta pb-3">
          <img src={logoContrato} alt="Contrato Terraplanagem" width={68} height={56} className="h-14 w-auto" />
          <div className="flex flex-col items-end gap-1">
            <span className="txt-secao">Folha de apontamento de turno</span>
            {/* O código é por onde a leitura acha a folha: grande e em mono. */}
            {f === null ? (
              <Carregando oQue="Carregando a folha de turno">
                <Barra className="my-1 h-5 w-32" />
              </Carregando>
            ) : (
              <span className="txt-secao">
                <span className="mono">{f.codigo}</span>
              </span>
            )}
          </div>
        </header>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
          {(
            [
              ['Máquina', f && `${f.equipamentoCodigo} · ${f.equipamentoDescricao}`, 'w-56'],
              ['Período', f && `${dataCurta(f.de)} a ${dataCurta(f.ate)}`, 'w-40'],
              ['Turnos', turnos && (turnos.length > 0 ? turnos.join(' · ') : 'Os da empresa'), 'w-32'],
              ['Empresa', f?.empresa ?? null, 'w-48'],
            ] as const
          ).map(([rotulo, valor, largura]) => (
            <div key={rotulo} className="flex flex-col">
              <dt className="txt-legenda font-normal text-tinta-secundaria">{rotulo}</dt>
              <dd className="txt-corpo font-semibold">{valor ?? <Barra className={`my-1 h-3 ${largura}`} />}</dd>
            </div>
          ))}
        </dl>

        <div className="flex flex-wrap items-center gap-6">
          <span className="txt-corpo">Preenchida com:</span>
          <span className="txt-corpo">☐ Caneta</span>
          <span className="txt-corpo">☐ Lápis</span>
          <span className="txt-apoio">
            Marque um. Uma linha por turno. Horas em decimal (6,5 = seis horas e meia); hora em branco vale zero. Se errar,
            risque uma vez e escreva ao lado.
          </span>
        </div>

        <table className="w-full border-collapse">
          <thead>
            <tr className="txt-legenda">
              <th className="w-10 border border-tinta p-2 text-left">Nº</th>
              <th className="w-20 border border-tinta p-2 text-left">Data (DD/MM)</th>
              <th className="w-16 border border-tinta p-2 text-left">Turno</th>
              <th className="border border-tinta p-2 text-right">Horímetro início</th>
              <th className="border border-tinta p-2 text-right">Horímetro fim</th>
              <th className="w-16 border border-tinta p-2 text-right">Produção (h)</th>
              <th className="w-16 border border-tinta p-2 text-right">Espera (h)</th>
              <th className="w-16 border border-tinta p-2 text-right">Desloc. (h)</th>
              <th className="w-16 border border-tinta p-2 text-right">Manut. (h)</th>
              <th className="border border-tinta p-2 text-left">Operador</th>
              {/* A causa da parada (0086): o código do catálogo, na linha com espera ou manutenção. */}
              <th className="w-20 border border-tinta p-2 text-left">Causa (código)</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: LINHAS }, (_, i) => (
              <tr key={i} className="h-10">
                <td className="txt-corpo border border-tinta p-2 tabular-nums">{i + 1}</td>
                {Array.from({ length: 10 }, (__, j) => (
                  <td key={j} className="border border-tinta" />
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {f === null ? (
          <div className="flex flex-col gap-2 py-1">
            <Barra className="h-3 w-full" />
            <Barra className="h-3 w-3/5" />
          </div>
        ) : (
          <p className="txt-apoio">
            {`Esta folha vale só para a máquina ${f.equipamentoCodigo}, no período impresso. A hora de motor não se escreve: o sistema a calcula pelos horímetros. Ao terminar, entregue para escanear: a leitura é conferida por quem escaneia antes de entrar no sistema.`}
          </p>
        )}

        {/* AS CAUSAS DA PARADA (0086): a legenda do código que vai na última
            coluna. Sem catálogo cadastrado, a coluna fica para escrever. */}
        {causas.length > 0 ? (
          <div className="flex flex-col gap-1">
            <span className="txt-corpo font-semibold">
              Causa da parada: escreva o código na linha que teve espera ou manutenção
            </span>
            {/* A legenda da R06 (p.69): o código em negrito, mono, e o nome ao lado, em linha. */}
            <ul className="txt-apoio flex flex-wrap gap-x-4 gap-y-1">
              {causas.map((c) => (
                <li key={c.id}>
                  <span className="mono font-semibold">{c.codigo}</span>
                  {` ${c.nome}`}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* O rodapé dos documentos (p.61): a Contrato e, assinando, a Atenas. */}
        <footer className="txt-legenda flex flex-wrap justify-between gap-2 border-t border-borda pt-2 font-normal text-tinta-secundaria">
          <span>Contrato Terraplanagem · Sistema desenvolvido pela Atenas</span>
          {f === null ? (
            <Barra className="my-1 h-2 w-32" />
          ) : (
            <span>{`Emitida em ${formatarData(new Date(f.emitidaEm))}`}</span>
          )}
        </footer>
      </div>
    </div>
  );
}
